import axios from 'axios';

// Temporary edge/origin failures (Cloudflare 52x, gateway errors, dropped connections).
// GET/HEAD requests are retried quietly; anything that changes data is never repeated.
export const FRIENDLY = "We couldn't reach the server just now. Your work is safe — please try again.";
const WAITS = [700, 1800];
const SAFE = new Set(['get', 'head', 'options']);

let listener = null;
export const onNetState = (fn) => { listener = fn; };
const emit = (s) => listener && listener(s);

const isHtmlOrEmpty = (d) => d == null || d === '' || (typeof d === 'string' && /<\/?(html|body|title|!doctype)/i.test(d))
  || (typeof Blob !== 'undefined' && d instanceof Blob && !/json/i.test(d.type || ''));

export const isTransient = (err) => {
  if (axios.isCancel?.(err) || err?.code === 'ERR_CANCELED') return false;
  const s = err?.response?.status;
  if (!err?.response) return err?.code === 'ERR_NETWORK' || err?.code === 'ECONNABORTED' || /network error/i.test(err?.message || '');
  const d = err.response.data;
  // Cloudflare's own error answers JSON-accepting clients with {cloudflare_error: true, ...}.
  if (d && typeof d === 'object' && d.cloudflare_error) return true;
  // Our backend never sends 502/504 or 52x; 503 with a JSON detail is ours and must reach the user.
  if (s === 502 || s === 504 || (s >= 520 && s <= 530)) return true;
  return s >= 500 && isHtmlOrEmpty(d);
};

const parseBlobJson = async (err) => {
  const d = err?.response?.data;
  if (typeof Blob !== 'undefined' && d instanceof Blob && /json/i.test(d.type || '')) {
    try { err.response.data = JSON.parse(await d.text()); } catch { /* leave as is */ }
  }
  return err;
};

const friendly = (err) => {
  if (err.response) err.response.data = { detail: FRIENDLY, code: 'server_unreachable' };
  else err.response = { status: 0, data: { detail: FRIENDLY, code: 'server_unreachable' }, headers: {} };
  err.message = FRIENDLY;
  return err;
};

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
let pending = 0;

axios.interceptors.response.use(
  (res) => res,
  async (err) => {
    const cfg = err?.config;
    await parseBlobJson(err);
    if (!cfg || !isTransient(err)) return Promise.reject(err);
    const safe = SAFE.has(String(cfg.method || 'get').toLowerCase());
    if (!safe) {
      emit({ kind: 'write-failed' });
      return Promise.reject(friendly(err));
    }
    cfg.__tries = (cfg.__tries || 0) + 1;
    if (cfg.__tries <= WAITS.length) {
      pending += 1;
      const t = setTimeout(() => emit({ kind: 'reconnecting' }), 1200);
      try {
        await sleep(WAITS[cfg.__tries - 1]);
        const res = await axios(cfg);
        return res;
      } finally {
        clearTimeout(t);
        pending -= 1;
        if (!pending) emit({ kind: 'ok' });
      }
    }
    // Retries used up: keep the request waiting until the user taps Retry (or dismisses).
    return new Promise((resolve, reject) => {
      emit({
        kind: 'offline',
        retry: () => { cfg.__tries = 0; resolve(axios(cfg)); },
        dismiss: () => reject(friendly(err)),
      });
    });
  },
);
